// Utility functions for handling @mentions

export interface MentionMatch {
  username: string;
  startIndex: number;
  endIndex: number;
}

/**
 * Parse @mentions from text
 * @param text - The text to parse
 * @returns Array of mention matches
 */
export function parseAtMentions(text: string): MentionMatch[] {
  const mentionRegex = /@(\w+)/g;
  const matches: MentionMatch[] = [];
  let match;

  while ((match = mentionRegex.exec(text)) !== null) {
    matches.push({
      username: match[1],
      startIndex: match.index,
      endIndex: match.index + match[0].length,
    });
  }

  return matches;
}

/**
 * Get the current @mention being typed at cursor position
 * @param text - The full text
 * @param cursorPosition - Current cursor position
 * @returns The mention being typed or null
 */
export function getCurrentMention(text: string, cursorPosition: number): { mention: string; startIndex: number } | null {
  // Look backwards from cursor to find @ symbol
  let atIndex = -1;
  for (let i = cursorPosition - 1; i >= 0; i--) {
    const char = text[i];
    if (char === '@') {
      atIndex = i;
      break;
    }
    if (char === ' ' || char === '\n') {
      // Hit whitespace before @, not in a mention
      break;
    }
  }

  if (atIndex === -1) return null;

  // Extract the mention text
  const mention = text.substring(atIndex + 1, cursorPosition);

  // Validate mention contains only valid characters
  if (!/^\w*$/.test(mention)) return null;

  return {
    mention,
    startIndex: atIndex,
  };
}

/**
 * Filter users based on mention query
 * @param users - Array of users
 * @param query - The mention query (without @)
 * @returns Filtered users
 */
export function filterUsersForMention(users: any[], query: string): any[] {
  if (!query) return users.slice(0, 5); // Show first 5 users if no query

  const lowerQuery = query.toLowerCase();
  return users
    .filter(user =>
      user.username.toLowerCase().includes(lowerQuery) ||
      user.displayName?.toLowerCase().includes(lowerQuery)
    )
    .slice(0, 5); // Limit to 5 results
}